import { intersection, isRegExp } from 'lodash-es';
import { ReactNode, useMemo } from 'react';
import validator from 'validator';

import {
  ContextActionIocType,
  EnabledContextActionFragment as ContextActionType,
} from 'core/graphql';

export type IocType =
  | 'domain'
  | 'email'
  | 'file'
  | 'git'
  | 'hash'
  | 'ip4'
  | 'ip6'
  | 'keyword'
  | 'mac'
  | 'recordProperty'
  | 'url';

export interface IocMatch {
  test?: ((str: string) => boolean) | RegExp;
  type: IocType;
}

export interface Match {
  type: IocType;
  content: string;
}

interface MatchedTypes {
  actions: ContextActionType[];
  entityType?: string;
  matches: Match[];
  recordField?: string;
}

// Reference: https://github.com/chriso/validator.js
const hashTypes: validator.HashAlgorithm[] = [
  'crc32',
  'crc32b',
  'md4',
  'md5',
  'ripemd128',
  'ripemd160',
  'sha1',
  'sha256',
  'sha384',
  'sha512',
  'tiger128',
  'tiger160',
  'tiger192',
];

const isHash = (hash: string) => {
  // Fix for MD5's which contain lowercase chars not matching
  const lower = hash.toLowerCase();
  return hashTypes.some(
    (type) => validator.isHash(hash, type) || validator.isHash(lower, type)
  );
};

export const defaultMatchers: IocMatch[] = [
  { type: 'ip4', test: (str) => validator.isIP(str, 4) },
  { type: 'ip6', test: (str) => validator.isIP(str, 6) },
  { type: 'domain', test: (str) => validator.isFQDN(str) },
  { type: 'email', test: (str) => validator.isEmail(str) },
  { type: 'url', test: (str) => validator.isURL(str) },
  { type: 'hash', test: (str) => isHash(str) },
  { type: 'mac', test: (str) => validator.isMACAddress(str) },
  /* eslint-disable prefer-regex-literals */
  {
    type: 'git',
    // https://github.com/bukharim96/pregx/blob/master/src/patterns/git-repo.js
    test: new RegExp(
      '((git|ssh|http(s)?)|(git@[\\w\\.]+))(:(//)?)([\\w\\.@\\:/\\-~]+)(\\.git)(/)?'
    ),
  },
  {
    type: 'file',
    // https://www.regextester.com/105245
    test: new RegExp('^/([A-z0-9-_+]+/)*([A-z0-9]+.(txt|zip))$'),
  },
  /* eslint-enable prefer-regex-literals */
];

function findMatches(
  value: string | number | ReactNode,
  customMatchers?: IocMatch[]
): Match[] {
  const combinedMatchers = customMatchers
    ? [...defaultMatchers, ...customMatchers]
    : defaultMatchers;

  // If there is no test function, assume the match type always passes.
  const validType = ['string', 'number'].includes(typeof value);
  if (!validType) {
    return [];
  }

  const stringValue = `${value}`;

  const matchedTypes = combinedMatchers
    .filter(({ test: matchTest }) =>
      matchTest
        ? isRegExp(matchTest)
          ? matchTest.test(stringValue)
          : matchTest(stringValue)
        : true
    )
    .map(({ type }) => type);

  return matchedTypes.map((type) => ({
    type,
    content: stringValue,
  }));
}

export function useIocMatches(
  value: string | number | undefined | null,
  customMatchers?: IocMatch[]
) {
  return useMemo(() => {
    if (value == null) {
      return [];
    }
    return findMatches(value, customMatchers);
  }, [value, customMatchers]);
}

export const useMatchedTypes = ({
  actions,
  entityType,
  matches,
  recordField,
}: MatchedTypes) =>
  useMemo(() => {
    // The `recordProperty` is considered deprecated in the IOC list (in favor
    // of `allRecordFields`), but still treat it as a valid IOC type.
    const matchMap = {
      domain: ContextActionIocType.Domain,
      hash: ContextActionIocType.Hash,
      ip4: ContextActionIocType.IpAddress,
      ip6: ContextActionIocType.IpAddress,
      recordProperty: ContextActionIocType.RecordProperty,
      url: ContextActionIocType.Url,
    };

    // Determine which context action types match the IOC content.
    const matchedIocTypes: ContextActionIocType[] = matches
      .filter(({ type }) => matchMap.hasOwnProperty(type))
      .map((match) => matchMap[match.type]);

    return actions.filter(
      ({ iocTypes, entityTypes, recordFields, allRecordFields }) => {
        // Check if there is a matching IOC.
        if (intersection(iocTypes, matchedIocTypes).length) {
          return true;
        }

        // Check if there is a matching Entity type or Record field.
        return (
          (entityType && entityTypes?.includes(entityType)) ||
          (recordField &&
            (allRecordFields || recordFields?.includes(recordField)))
        );
      }
    );
  }, [actions, entityType, matches, recordField]);
